import React, { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { ContentWrapper } from '../Style'
import CommunicationInstructions from '../Partials/Communicationassmt/instruction'
import BehaviourInstructions from '../Partials/Behaviourassmt/instruction'
import AptitudeInstructions from '../Partials/aptitudeassmt/instruction'
import { 
  fetchActiveAssessments,
  fetchAssessmentQuestions,
  saveQuestionResponse,
  fetchCommunicationInstructions,
  loadDirectAssessment
} from '../actions'
import selectors from '../selectors'

const DirectAssessment = () => {
  const { assessment_assigned_id, assessment_set_id } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  
  const [assessment, setAssessment] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [assessmentProcessed, setAssessmentProcessed] = useState(false)

  // Redux selectors
  const { studentDetails } = useSelector(state => state?.studentData)
  const studentId = studentDetails?.id
  const activeAssessments = useSelector(selectors.getActiveAssessments)
  const questions = useSelector(selectors.getQuestions)
  const totalQuestions = useSelector(selectors.getTotalQuestions)
  const questionsLoading = useSelector(selectors.getLoading)
  const questionsError = useSelector(selectors.getError)
  const responses = useSelector(selectors.getResponses)

  // Debug logging - runs only once on component mount
  useEffect(() => {
    console.log('🔍 DirectAssessment Component Mounted')
    console.log('📋 URL Params:', { assessment_assigned_id, assessment_set_id })
    console.log('👤 Student ID:', studentId)
  }, [])

  // Memoized function to load direct assessment
  const loadDirectAssessment = useCallback(async () => {
    if (!studentId || !assessment_assigned_id || !assessment_set_id || assessmentProcessed) {
      return
    }

    try {
      setLoading(true)
      setError(null)

      console.log('🔄 Loading direct assessment...')
      
      // Fetch active assessments to find the matching one
      await dispatch(fetchActiveAssessments(studentId, {}))
      
      setAssessmentProcessed(true)
    } catch (err) {
      console.error('❌ Error loading direct assessment:', err)
      setError('Failed to load assessment. Please check the URL and try again.')
      setLoading(false)
    }
  }, [studentId, assessment_assigned_id, assessment_set_id, dispatch, assessmentProcessed])

  // Load assessment on component mount
  useEffect(() => {
    loadDirectAssessment()
  }, [loadDirectAssessment])

  // Process active assessments to find matching assessment
  useEffect(() => {
    if (activeAssessments && activeAssessments.length > 0 && !assessment) {
      const matchingAssessment = activeAssessments.find(
        assess => 
          assess.assessment_assigned_id === assessment_assigned_id && 
          assess.assessment_set_id === assessment_set_id
      )

      if (matchingAssessment) {
        console.log('✅ Found matching assessment:', matchingAssessment)
        
        // Set the current assessment in Redux state for proper API calls
        dispatch({
          type: 'SET_CURRENT_ASSESSMENT_ID',
          payload: {
            assessment_id: matchingAssessment.assessment_id || matchingAssessment.id,
            assessment_set_id: matchingAssessment.assessment_set_id,
            assessment_assigned_id: matchingAssessment.assessment_assigned_id,
            allowProctoring: matchingAssessment.allowProctoring || false,
            title: matchingAssessment.title,
            type: matchingAssessment.type,
            isPractice: false,
          },
        })
        
        setAssessment(matchingAssessment)
        setLoading(false)
      } else {
        console.log('❌ No matching assessment found')
        setError('Assessment not found or not accessible.')
        setLoading(false)
      }
    }
  }, [activeAssessments, assessment_assigned_id, assessment_set_id, assessment, dispatch])

  // Memoized handler functions
  const handleBackToAssessments = useCallback(() => {
    navigate('/assessment')
  }, [navigate])

  // Memoized dispatched action creators to prevent infinite re-renders
  const dispatchFetchAssessmentQuestions = useCallback((assessmentAssignedId) => {
    return dispatch(fetchAssessmentQuestions(assessmentAssignedId))
  }, [dispatch])

  const dispatchSaveQuestionResponse = useCallback((questionId, answer, assessment_assigned_id, subQuestionId, questionType) => {
    return dispatch(saveQuestionResponse(questionId, answer, assessment_assigned_id, subQuestionId, questionType))
  }, [dispatch])

  const dispatchFetchCommunicationInstructions = useCallback((assessmentSetId) => {
    return dispatch(fetchCommunicationInstructions(assessmentSetId))
  }, [dispatch])

  const renderAssessmentInstructions = useCallback(() => {
    if (!assessment) return null

    const commonProps = {
      assessment,
      questions,
      totalQuestions,
      loading: questionsLoading,
      error: questionsError,
      responses,
      fetchAssessmentQuestions: dispatchFetchAssessmentQuestions,
      saveQuestionResponse: dispatchSaveQuestionResponse,
      studentId,
      setAssessment,
    }

    switch (assessment.type) {
      case 'Behavior':
        return <BehaviourInstructions {...commonProps} />
      case 'Communication':
        return (
          <CommunicationInstructions 
            {...commonProps}
            fetchCommunicationInstructions={dispatchFetchCommunicationInstructions}
          />
        )
      case 'Aptitude':
        return <AptitudeInstructions {...commonProps} />
      default:
        return (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <h3>Unsupported Assessment Type</h3>
            <p>Assessment type "{assessment.type}" is not supported.</p>
            <button onClick={handleBackToAssessments}>
              Back to Assessments
            </button>
          </div>
        )
    }
  }, [
    assessment,
    questions,
    totalQuestions,
    questionsLoading,
    questionsError,
    responses,
    studentId,
    dispatchFetchAssessmentQuestions,
    dispatchSaveQuestionResponse,
    dispatchFetchCommunicationInstructions,
    handleBackToAssessments
  ])

  // Loading state
  if (loading) {
    return (
      <ContentWrapper>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <h3>Loading Assessment...</h3>
          <p>Please wait while we load your assessment.</p>
        </div>
      </ContentWrapper>
    )
  }

  // Error state
  if (error) {
    return (
      <ContentWrapper>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <h3>Error Loading Assessment</h3>
          <p>{error}</p>
          <button onClick={handleBackToAssessments}>
            Back to Assessments
          </button>
        </div>
      </ContentWrapper>
    )
  }

  // Render assessment instructions
  if (assessment) {
    return renderAssessmentInstructions()
  }

  // Fallback
  return (
    <ContentWrapper>
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <h3>Assessment Not Found</h3>
        <p>The requested assessment could not be found.</p>
        <button onClick={handleBackToAssessments}>
          Back to Assessments
        </button>
      </div>
    </ContentWrapper>
  )
}

export default DirectAssessment
